import { BookingStatus } from "types/booking-status";
import { MenuStatus } from "types/menu";
import { WebBooking } from "types/web-booking";

/**
 * Returns the rule id of a booked activity that still needs a menu selection, or undefined
 * when nothing is missing.
 *
 * The activity modal enforces this today by holding the guest on its menu step, but that
 * state only lives as long as the modal is mounted. Browser back/forward, Esc or a reload
 * all drop it while the reservation itself lives on, which lets the guest confirm without
 * a menu — and, because the payment amount is derived from the menu selection, without
 * paying either.
 *
 * Deriving the requirement from the booking instead means it survives all of those.
 *
 * The conditions below deliberately mirror the ones the modal uses to decide whether to
 * show its menu step at all, so this can only block a guest the modal would already have
 * held back:
 * - the booked article must be connected to a menu. In experiences every sibling activity
 *   shares the same article menuGuid, so answering the shared menu satisfies all of them.
 * - the menu must exist and actually have items to choose from. A rule that requires a
 *   selection from an empty menu gets no menu step, so blocking on it would be a dead end.
 *
 * Wait list bookings are excluded: they get a menu too, but the wait list flow never offers
 * a menu step, so the menu is answered later (if at all).
 *
 * Reads plain data only — no WebBooking methods — so it stays safe on any booking-shaped
 * value, including a cache entry that was seeded without being wrapped in the class.
 */
export function findRuleIdMissingMenuSelection(booking?: WebBooking | null): number | undefined {
    if (!booking || booking.status === BookingStatus.WaitList) return undefined;

    const article = booking.articles?.find((article) => {
        if (!article.menuGuid) return false;

        const rule = booking.webBookingRules?.find((rule) => rule.id === article.ruleId);
        if (!rule?.requireMenuSelection) return false;

        const menu = booking.menus?.find((menu) => menu.publicId === article.menuGuid);
        if (!menu) return false;

        const hasItemsToChooseFrom = menu.groups?.some((group) => (group.items?.length ?? 0) > 0);
        if (!hasItemsToChooseFrom) return false;

        return menu.status !== MenuStatus.Confirmed;
    });

    return article?.ruleId;
}
